/**
 * Session state: tokens, the signed-in account, and the active tree.
 *
 * Two decisions worth knowing:
 *
 * **Where tokens live.** The access token is kept in memory only — a page reload
 * re-derives it from the refresh token. The refresh token is in localStorage,
 * because the alternative is asking eighty-seven relatives to sign in every time
 * they close a tab, and a family calendar that logs you out is a family calendar
 * nobody opens. The mitigation is that refresh tokens rotate and the previous one
 * is blacklisted, so a stolen token is single-use and its theft is detectable.
 *
 * **The active tree is session state, not route state.** A user belongs to
 * several trees — their own, their spouse's, each parent's. The switcher changes
 * one value here and every subsequent request carries the new `X-Tree-ID`.
 */

const REFRESH_KEY = 'linyay.refresh';
const TREE_KEY = 'linyay.tree';

class Session extends EventTarget {
  constructor() {
    super();

    /** @type {string|null} in memory only; never persisted */
    this.accessToken = null;

    /** @type {string|null} */
    this.refreshToken = readStorage(REFRESH_KEY);

    /** @type {string|null} */
    this.activeTreeId = readStorage(TREE_KEY);

    /** @type {object|null} the /auth/me/ payload */
    this.user = null;

    /** @type {Array<object>} every tree this user belongs to — the switcher's data */
    this.trees = [];

    /** @type {string|null} role in the active tree: owner | admin | member | child */
    this.role = null;

    /** @type {string|null} the caller's Person node in the active tree, if claimed */
    this.personId = null;
    /** Who this account is on the active tree, in words. See the account screen. */
    this.personName = null;
  }

  get isAuthenticated() {
    return Boolean(this.accessToken || this.refreshToken);
  }

  /** True when the caller can invite relatives and change roles. */
  get isAdmin() {
    return this.role === 'owner' || this.role === 'admin';
  }

  /**
   * True for an under-13, parent-managed account.
   *
   * Drives the client side of the COPPA / GDPR-K rules: no messaging outside the
   * tree, never in money flows. The server enforces them regardless — this only
   * avoids showing a child a button that would be refused.
   */
  get isChild() {
    return this.role === 'child';
  }

  /** @type {object|null} the active tree's summary row */
  get activeTree() {
    return this.trees.find((tree) => tree.tree_id === this.activeTreeId) ?? null;
  }

  setTokens({ access, refresh }) {
    this.accessToken = access ?? null;
    if (refresh) {
      this.refreshToken = refresh;
      writeStorage(REFRESH_KEY, refresh);
    }
    this.emit('tokens');
  }

  /** Called after sign-in or invitation accept, when the server resolves a tree. */
  setContext({ treeId, role, personId }) {
    if (treeId !== undefined) {
      this.activeTreeId = treeId;
      writeStorage(TREE_KEY, treeId);
    }
    if (role !== undefined) this.role = role;
    if (personId !== undefined) this.personId = personId;
    this.emit('context');
  }

  setUser(user) {
    this.user = user;
    this.trees = user?.trees ?? [];

    // Adopt a tree the user is actually a member of.
    //
    // Covers three cases with one rule. A stored tree they have been removed from
    // must not stay active, or the next request is refused and every screen errors at
    // once. A stored tree that no longer exists is the same problem. And no stored
    // tree at all — a fresh sign-in, or a client recovering from a refusal — should
    // land on their family rather than nowhere.
    //
    // The condition deliberately does not require `activeTreeId` to be set: the
    // recovery path clears it first, and an earlier version of this check then skipped
    // itself and left the client with no tree.
    const stored = this.activeTreeId;
    if (!this.trees.some((membership) => membership.tree_id === stored)) {
      this.activeTreeId = this.trees[0]?.tree_id ?? null;
      writeStorage(TREE_KEY, this.activeTreeId);
    }

    const active = this.activeTree;
    if (active) {
      this.role = active.role;
      this.personId = active.person_id ?? null;
      this.personName = active.person_name ?? null;
    }

    this.emit('user');
  }

  /**
   * Switch trees.
   *
   * Only the stored id changes; the next request carries the new header and the
   * server validates membership. No re-authentication, and no token reissue.
   */
  switchTree(treeId) {
    const target = this.trees.find((tree) => tree.tree_id === treeId);
    if (!target) throw new Error(`Not a member of tree ${treeId}`);

    this.activeTreeId = treeId;
    writeStorage(TREE_KEY, treeId);
    this.role = target.role;
    this.personId = target.person_id ?? null;
    this.personName = target.person_name ?? null;

    this.emit('tree-changed');
  }

  clear() {
    this.accessToken = null;
    this.refreshToken = null;
    this.user = null;
    this.trees = [];
    this.role = null;
    this.personId = null;
    this.personName = null;
    // The active tree is kept deliberately: after a re-login the user lands back
    // in the family they were looking at, which is almost always what they want.
    removeStorage(REFRESH_KEY);
    this.emit('signed-out');
  }

  emit(type, detail) {
    this.dispatchEvent(new CustomEvent(type, { detail }));
  }

  /** Convenience wrapper so screens can subscribe without touching EventTarget. */
  on(type, handler) {
    this.addEventListener(type, handler);
    return () => this.removeEventListener(type, handler);
  }
}

// localStorage throws in private-browsing modes on some mobile browsers. A
// family app must degrade to a session that lasts until the tab closes rather
// than refusing to load.
function readStorage(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key, value) {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function removeStorage(key) {
  writeStorage(key, null);
}

export const session = new Session();
